// src/services/api.js
import { API_ENDPOINT } from "../aws-config";
import { getStoredToken } from "../utils/authStorage";

// Auth is the custom JWT/Postgres system (see AuthContext.jsx / auth/handler.py
// Lambda) — the same one winery-owner/road-rep/admin already use, now extended
// with role='traveler'. This is NOT the Cognito/Amplify session — that scaffold
// (aws-config.js, edge.yaml UserPool) exists in infra but was never wired to
// any API Gateway authorizer or backend verification, so it's intentionally
// unused here.
//
// Sessions are scoped per role (see utils/authStorage.js) — a winery-owner
// login and a traveler login can coexist in the same browser, so every call
// below says which role's token it wants rather than relying on a single
// shared "whoever's logged in" token.

const RETRYABLE_STATUSES = new Set([503, 429]);
const MAX_RETRIES = 3;

async function request(path, options = {}, role = "traveler", attempt = 0) {
  const token = getStoredToken(role);
  const headers = {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  const res = await fetch(`${API_ENDPOINT}${path}`, {
    ...options,
    headers,
  });
  // 503/429 here are almost always a brief Lambda concurrency throttle
  // (the account's shared concurrency pool is small and gets saturated by
  // bursts across the app's many functions) rather than a real outage —
  // back off and retry a few times before surfacing an error to the user.
  if (RETRYABLE_STATUSES.has(res.status) && attempt < MAX_RETRIES) {
    await new Promise(r => setTimeout(r, 500 * (attempt + 1)));
    return request(path, options, role, attempt + 1);
  }
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw new Error(err.message || "Request failed");
  }
  return res.json();
}

// ── Traveler API ──────────────────────────────────────────────────────────────
export const api = {
  // Send a chat message to the UI agent
  // conversationHistory: array of {role, content} from previous turns
  chat: (sessionId, message, conversationHistory = []) =>
    request("/chat", {
      method: "POST",
      body: JSON.stringify({
        session_id: sessionId,
        message,
        conversation_history: conversationHistory,
      }),
    }),

  // Generate tour plan from form preferences
  postPlan: (preferences) =>
    request("/plan", {
      method: "POST",
      body: JSON.stringify({ preferences }),
    }),

  // Get stored tour plan by plan ID
  getPlan: (planId) =>
    request(`/plan/${planId}`),

  // Make a tied alternate route (routeKey = route names joined by "|") the
  // plan's main itinerary; returns { tour }.
  switchAlternative: (planId, routeKey) =>
    request(`/plan/${planId}/switch-alternative`, {
      method: "POST",
      body: JSON.stringify({ route_key: routeKey }),
    }),

  // Booking API — one endpoint, two modes (see mcp-booking/handler.py's
  // module docstring). Plain call (no opts.planId) = legacy month-of-
  // availability, no trip context. With opts.planId/legIndex/departureAt =
  // trip-aware mode: runs the exact same live per-click feasibility
  // calculation the "Book this tour" stepper uses (occasion_type/
  // party_size/earliest-arrival/window), called fresh every time, never
  // cached. Both the ad-hoc "Book tasting" flow and the stepper call this
  // same function so they can never disagree on what's bookable.
  // opts.tripRef scopes the dwell-time math to one checkout — pass the same
  // tripRef to every call for a given (plan_id, departure_at) so bookings
  // made via either flow count toward each other's feasibility math.
  getBookingAvailability: (wineryId, opts = {}) => {
    const { year, month, partySize = 2, planId, legIndex, departureAt, tripRef } = opts;
    const qs = new URLSearchParams({ winery_id: wineryId, party_size: String(partySize) });
    if (planId != null && legIndex != null && departureAt) {
      qs.set("plan_id", planId);
      qs.set("leg_index", String(legIndex));
      qs.set("departure_at", departureAt);
      if (tripRef) qs.set("trip_ref", tripRef);
    } else {
      const now = new Date();
      qs.set("year", String(year ?? now.getFullYear()));
      qs.set("month", String(month ?? now.getMonth() + 1));
    }
    return request(`/booking/availability?${qs.toString()}`);
  },

  createBooking: (data) =>
    request("/booking", { method: "POST", body: JSON.stringify(data) }),

  // Get tour plan by session ID (legacy)
  getTour: (sessionId) =>
    request(`/tour/${sessionId}`),

  // Save a tour (registered users only)
  saveTour: (tourData) =>
    request("/tour", {
      method: "POST",
      body: JSON.stringify(tourData),
    }),

  // List saved tours for the current user (registered travelers only)
  myTours: () =>
    request("/tours").then(r => r.tours || []),

  // Remove a plan from My Tours
  deleteTour: (planId) =>
    request(`/tours/${planId}`, { method: "DELETE" }),

  // List the current traveler's own bookings (registered travelers only)
  myBookings: () =>
    request("/bookings/mine").then(r => r.bookings || []),

  // Cancel one of the current traveler's own bookings
  cancelBooking: (bookingId) =>
    request(`/bookings/mine/${bookingId}`, { method: "DELETE" }),

  // Update the logged-in traveler's account settings (preferences)
  updateMe: (data) =>
    request("/auth/me", { method: "PUT", body: JSON.stringify(data) }),

  // GDPR data portability (Art. 20) — download everything tied to this
  // account (profile, bookings, tour plans) as JSON. role lets a
  // winery_owner call this for their own manager account too.
  exportMyData: (role = "traveler") =>
    request("/auth/me/export", {}, role),

  // GDPR self-service erasure (Art. 17) — anonymizes the account in place.
  deleteAccount: (role = "traveler") =>
    request("/auth/me", { method: "DELETE" }, role),

  // Guest (email-only) booking — self-service early deletion of the
  // pseudonymized contact record. No auth: the unguessable token from the
  // emailed link is the credential (see mcp-booking/handler.py::
  // get_guest_contact_by_token / delete_guest_contact).
  getGuestContact: (token) =>
    request(`/booking/guest-contact?token=${encodeURIComponent(token)}`),

  deleteGuestContact: (token) =>
    request("/booking/guest-contact/delete", {
      method: "POST",
      body: JSON.stringify({ token }),
    }),

  // ── Winery Owner API ───────────────────────────────────────────────────────
  getWinery: () =>
    request("/winery/me", {}, "winery_owner"),

  updateWinery: (data) =>
    request("/winery/me", {
      method: "PUT",
      body: JSON.stringify(data),
    }, "winery_owner"),

  getAvailability: (from, to) =>
    request(`/winery/me/availability?from=${from}&to=${to}`, {}, "winery_owner"),

  setAvailability: (slot) =>
    request("/winery/me/availability", {
      method: "POST",
      body: JSON.stringify(slot),
    }, "winery_owner"),

  getBookings: () =>
    request("/winery/me/bookings", {}, "winery_owner"),

  // Winery photo gallery (022-winery-images.sql). Upload is 2-step, same
  // pattern as wine-label images: get a presigned S3 PUT url, PUT the file
  // there directly, then register the key. getWineryImages is intentionally
  // unauthenticated-safe (no role arg) since it's also read on the public
  // tour-results dwelling card.
  getWineryImageUploadUrl: (wineryId, contentType) =>
    request(`/manage/winery/${wineryId}/image-upload-url`, {
      method: "POST",
      body: JSON.stringify({ content_type: contentType }),
    }, "winery_owner"),

  getWineryImages: (wineryId) =>
    request(`/manage/winery/${wineryId}/images`),

  addWineryImage: (wineryId, key, makeTitle = false) =>
    request(`/manage/winery/${wineryId}/images`, {
      method: "POST",
      body: JSON.stringify({ key, make_title: makeTitle }),
    }, "winery_owner"),

  setWineryTitleImage: (wineryId, imageId) =>
    request(`/manage/winery/${wineryId}/images/${imageId}/title`, { method: "PUT" }, "winery_owner"),

  deleteWineryImage: (wineryId, imageId) =>
    request(`/manage/winery/${wineryId}/images/${imageId}`, { method: "DELETE" }, "winery_owner"),

  // Bound wines for a given winery (public — used in the traveler-facing
  // winery detail modal on the tour results page)
  getWineryWines: (wineryId) =>
    request(`/manage/winery/${wineryId}/wines`),

  // Wines bound to any winery on a road (public) — one row per
  // winery/wine binding, so callers dedupe by wine_id themselves. Used
  // for the wine-filter checkbox grid on the Plan page.
  searchWines: (roadSlug) =>
    request(`/manage/wines/search?road_slug=${encodeURIComponent(roadSlug)}`),
};
